import type { ReplayMessage, ReplayParticipant } from '@wehunt/ui'
import type { HuntReplayChatDto } from './huntReplayChatDto'

/**
 * Chat as the viewer consumes it, keeping only messages it can actually draw.
 *
 * A bubble is anchored to its sender's marker, so a message whose author is
 * absent or matches nobody on the hunt has nowhere to go. Dropping those here,
 * in the mapper, is what lets the viewer treat every message it holds as
 * drawable — and it keeps the filter row's count honest, since the count is
 * taken from the same list.
 *
 * The join works because the archive's carrier id IS the user id for a hunter,
 * which is the id the chat stores as a message's author. Dogs carry a dog id
 * and send nothing, so restricting the match to hunters costs no real message
 * and states the intent.
 *
 * Every field is checked rather than trusted. The replay is drawn whether or not
 * the chat resolves, and a mapper that threw on an unexpected payload would take
 * the whole hunt down with it — turning the one part of this feature that is
 * allowed to fail into the part that breaks everything else.
 */
export function chatDtoToMessages(
  dto: HuntReplayChatDto,
  participants: readonly ReplayParticipant[],
): ReplayMessage[] {
  const hunters = new Set(
    participants.filter((p) => p.role === 'hunter').map((p) => p.id),
  )

  const messages: ReplayMessage[] = []
  for (const message of Array.isArray(dto?.messages) ? dto.messages : []) {
    const authorId = message?.author ?? undefined
    if (typeof authorId !== 'string' || !hunters.has(authorId)) continue
    if (typeof message.text !== 'string' || message.text === '') continue

    const at = typeof message.at === 'string' ? Date.parse(message.at) : Number.NaN
    // An unparseable timestamp has no place on a timeline, and NaN would
    // compare false against every cursor — silently invisible for the wrong
    // reason rather than the right one.
    if (!Number.isFinite(at)) continue

    messages.push({ at, authorId, text: message.text })
  }
  return messages
}
